import Grid from "@material-ui/core/Grid";
import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { GlobalHotKeys } from "react-hotkeys";
import Box from "@material-ui/core/Box";
import classnames from "classnames";
import Typography from "@material-ui/core/Typography";
import { Settings32 } from "@carbon/icons-react";
import { EventStatus } from "@prestosports/prestostats-core/Common";
import gameOverallStyles from "../styles/GameOverall.styles";
import NumberFormat from "../../../util/number-format";
import { useGameContextContext } from "../../../state/game-context/context";
import GameClockView from "./GameClockView";
import GameControl from "./GameControl";
import { useFootballContext } from "../../../state/football-play/context";
import { useAppContext } from "../../../state/app/context";
import gameClockViewStyles from "../styles/GameClockView.styles";
import commonStyles from "../../common/styles/Common.styles";
import NewPeriodNotification from "../../common/components/NewPeriodNotification";
import Stream from "../../common/components/Stream";
import { useEventContext } from "../../../state/events/context";
import { toCanonicalSeconds, toDisplaySeconds } from "./utils/PlayUtils";
import {
	canOpenTimeDialog,
	showsClockControls,
	showsGameClock
} from "./utils/OvertimeUtils";
import { useAuthContext } from "../../../state/authentication/context";
import useRunningClock from "../../../state/game-context/use-running-clock";
import GameClockControls from "./GameClockControls";

const GameOverallKeyListener = ({
	showEditDialog,
	runningClockEnabled,
	onToggleClock
}) => {
	const keyMap = {
		CLOCK: ["shift+c"],
		...(runningClockEnabled ? { TOGGLE_CLOCK: ["space"] } : {})
	};

	const handlers = {
		CLOCK: (event) => {
			event.preventDefault();
			showEditDialog();
		},
		...(runningClockEnabled
			? {
					TOGGLE_CLOCK: (event) => {
						event.preventDefault();
						onToggleClock();
					}
			  }
			: {})
	};

	return <GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />;
};

const GameOverall = ({ size, preview = false }) => {
	const commonClasses = commonStyles();
	const classes = gameOverallStyles({ size });
	const gameClockClasses = gameClockViewStyles({ size });

	const { state: appState } = useAppContext();
	const isInsertingPlay = !!appState.insertPlay();

	const { state: authState } = useAuthContext();
	const isSuperUser = authState.isSuperUser();

	const { state, setTimeAndPeriod, showTimeConfig } = useGameContextContext();
	const { isRunning, toggleClock, runningClockEnabled } = useRunningClock({
		tick: true
	});
	const gameConfig = state.gameConfig();
	const triggerPAT = state.triggerPAT();
	const triggerEndTime = state.triggerEndTime();
	const gameClock = state.gameClock();
	const yardsToFirstDown = state.yardsToFirstDown();
	const down = state.down();
	const period = state.period();
	const periodNumber = period || 1;
	const validationErrors = state.validationErrors();
	const timeConfig = state.showTimeConfig();
	const overtimePeriod = state.overtimePeriod();
	const fbOtDisabled = state.fbOtDisabled();
	const triggerKO = state.triggerKO();
	const isGameInitialized = state.isGameInitialized();
	const missingGameData = state.missingGameData();

	const {
		state: footballState,
		gameControl,
		insertTimePlay
	} = useFootballContext();
	const canChangePeriods = footballState.canChangePeriods();
	const isPatAttempt = footballState.isPatAttempt();

	const { state: eventState } = useEventContext();
	const currentEvent = eventState.currentEvent();
	const canEditCurrentEvent = eventState.canEditCurrentEvent();
	// No play/pause where no clock is shown (an untimed overtime).
	const clockControlsShown = showsClockControls({
		runningClockEnabled,
		overtimePeriod,
		fbOtDisabled
	});

	const onCancel = () => {
		showTimeConfig(false);
	};

	// A click on the scoreboard and Shift+C open the time dialog alike.
	const openTimeDialog = () => {
		if (
			canOpenTimeDialog({
				canEditCurrentEvent,
				isGameInitialized,
				overtimePeriod,
				fbOtDisabled,
				sportId: currentEvent?.sportId,
				gameConfig
			})
		) {
			showTimeConfig(true);
		}
	};

	// The dialog shows and collects display time; storage stays canonical
	// countdown (ascending Clock Type is display-only).
	const onConfirm = (values) => {
		const canonical = {
			...values,
			seconds: toCanonicalSeconds(values.seconds, gameConfig, values.period)
		};
		if (isInsertingPlay) {
			insertTimePlay(canonical);
		} else {
			setTimeAndPeriod(canonical);
		}
	};

	const getDownMessage = () => {
		if (triggerEndTime) return "End";
		if (triggerKO) return "Kickoff";
		if (isPatAttempt || triggerPAT) return "PAT";
		return `${NumberFormat.withOrdinal(
			down
		).toUpperCase()} & ${yardsToFirstDown}`;
	};

	return (
		<Box className={classnames(classes.container, { small: size === "small" })}>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				spacing={1}
				onClick={openTimeDialog}
				wrap="nowrap"
				id="game-overall"
				className={classes.controls}
			>
				{!canEditCurrentEvent && (
					<Grid item>
						<Typography variant="inherit" className={gameClockClasses.clockText}>
							{EventStatus.resolve(currentEvent)}
						</Typography>
					</Grid>
				)}
				{canEditCurrentEvent && (
					<>
						{!preview && (
							<Grid item>
								<IconButton
									aria-label="Game control settings"
									id="game-control-gear-btn"
									disabled={!isGameInitialized && !missingGameData.length}
									color="secondary"
									onClick={(e) => {
										e.stopPropagation();
										gameControl();
									}}
								>
									<Settings32 fontSize="inherit" />
								</IconButton>
							</Grid>
						)}

						{!preview && clockControlsShown && (
							<Grid item>
								<GameClockControls isRunning={isRunning} onToggle={toggleClock} />
							</Grid>
						)}

						<Grid item>
							{showsGameClock({ overtimePeriod, fbOtDisabled }) ? (
								<GameClockView size={size} />
							) : (
								<Typography variant="inherit" className={gameClockClasses.clockText}>
									OT
								</Typography>
							)}
						</Grid>

						<Grid item>
							<Grid container direction="column" className={classes.periodAndDown}>
								<Grid item>
									{overtimePeriod ? (
										<span>OT {overtimePeriod}</span>
									) : (
										<span>QTR {periodNumber}</span>
									)}
								</Grid>
								<Grid item className={classes.down}>
									<span>{getDownMessage()}</span>
								</Grid>
							</Grid>
						</Grid>

						{isSuperUser && (
							<Grid item>
								<Stream />
							</Grid>
						)}
					</>
				)}
			</Grid>

			<Dialog
				open={timeConfig}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				classes={{
					paper: classes.dialogPaper
				}}
				onClose={onCancel}
			>
				<DialogTitle id="alert-dialog-title">
					Time
					<IconButton
						aria-label="close"
						className={commonClasses.closeButton}
						onClick={onCancel}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent className={classes.dialogContent}>
					<GameControl
						seconds={Math.floor(
							toDisplaySeconds(
								(typeof gameClock?.now === "function"
									? gameClock.now()
									: gameClock?.now || 0) / 1000,
								gameConfig,
								periodNumber
							)
						)}
						period={periodNumber}
						onConfirm={onConfirm}
						onCancel={onCancel}
						validationErrors={validationErrors}
						periodsDisabled={isInsertingPlay || !canChangePeriods}
					/>
				</DialogContent>
			</Dialog>

			<NewPeriodNotification />

			<GameOverallKeyListener
				showEditDialog={openTimeDialog}
				runningClockEnabled={clockControlsShown}
				onToggleClock={toggleClock}
			/>
		</Box>
	);
};

export default GameOverall;
